import React, { useEffect, useState } from "react";
import PortalLayout, { usePortal } from "../components/PortalLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { portalApi } from "../lib/portalApi";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, X, Copy, Check, ShieldCheck } from "lucide-react";

// Public-facing role labels per Iter 34 spec
const ROLES = [
  ["super_admin",       "Super Admin"],
  ["master_admin",      "Master Admin"],
  ["sales_demo",        "Sales Demo"],
  ["municipality_user", "Client"],
  ["auditor",           "Auditor"],
  ["department_user",   "Department User"],
];

const ROLE_LABEL = Object.fromEntries(ROLES);

const Inner = () => {
  const { orgs } = usePortal();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "municipality_user", organization_id: "",
  });
  const [tempPwdModal, setTempPwdModal] = useState(null); // { email, temporary_password }
  const [copied, setCopied] = useState(false);
  const [accessModal, setAccessModal] = useState(null); // { user_id, email, page_access: [] }
  const [availablePages, setAvailablePages] = useState([]);
  const [accessBusy, setAccessBusy] = useState(false);

  const load = () => portalApi.listUsers().then(r => setUsers(r.users || []));
  useEffect(() => {
    load();
    api.get("/admin/users/pages")
      .then(r => setAvailablePages(r.data?.pages || []))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const create = async (e) => {
    e.preventDefault();
    try {
      await portalApi.createUser({ ...form, organization_id: form.organization_id || null });
      toast.success(`User ${form.email} created`);
      setForm({ email: "", password: "", name: "", role: "municipality_user", organization_id: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Delete ${email}?`)) return;
    try { await portalApi.deleteUser(id); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const changeRole = async (user_id, role) => {
    try {
      await api.patch(`/admin/users/${user_id}/role`, { role });
      toast.success(`Role updated to ${ROLE_LABEL[role] || role}`);
      setUsers(us => us.map(u => u.id === user_id ? { ...u, role } : u));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Role change failed");
    }
  };

  const toggleStatus = async (user_id, currentStatus) => {
    const next = currentStatus === "inactive" ? "active" : "inactive";
    try {
      await api.patch(`/admin/users/${user_id}/status`, { status: next });
      toast.success(next === "active" ? "User enabled" : "User disabled");
      setUsers(us => us.map(u => u.id === user_id ? { ...u, status: next } : u));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status change failed");
    }
  };

  const resetPassword = async (user_id, email) => {
    if (!window.confirm(`Reset password for ${email}? A new temporary password will be generated.`)) return;
    try {
      const r = await api.post(`/admin/users/${user_id}/reset-password`);
      setTempPwdModal({ email: r.data.email, temporary_password: r.data.temporary_password });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const copyTempPwd = async () => {
    try {
      await navigator.clipboard.writeText(tempPwdModal.temporary_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Could not copy — select manually."); }
  };

  const openAccess = (u) => {
    setAccessModal({
      user_id: u.id,
      email: u.email,
      page_access: Array.isArray(u.page_access) ? [...u.page_access] : [],
    });
  };

  const toggleAccessPage = (page) => {
    setAccessModal(prev => {
      const set = new Set(prev.page_access);
      set.has(page) ? set.delete(page) : set.add(page);
      return { ...prev, page_access: Array.from(set) };
    });
  };

  const selectAllPages = () => setAccessModal(prev => ({ ...prev, page_access: [...availablePages] }));
  const clearAllPages  = () => setAccessModal(prev => ({ ...prev, page_access: [] }));

  const saveAccess = async () => {
    if (!accessModal) return;
    setAccessBusy(true);
    try {
      await api.patch(`/admin/users/${accessModal.user_id}/access`, {
        page_access: accessModal.page_access,
      });
      toast.success(`Page access updated for ${accessModal.email}`);
      setUsers(us => us.map(u => u.id === accessModal.user_id
        ? { ...u, page_access: accessModal.page_access } : u));
      setAccessModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Access update failed");
    }
    setAccessBusy(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric">User Management</div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Users & Permissions</h1>
      </div>

      <form onSubmit={create} className="glass p-6 grid md:grid-cols-5 gap-3" data-testid="user-create-form">
        <Input required type="email" value={form.email} onChange={set("email")} placeholder="email"
          className="bg-navy-deep border-gold-dim/40" data-testid="user-email" />
        <Input required value={form.name} onChange={set("name")} placeholder="full name"
          className="bg-navy-deep border-gold-dim/40" data-testid="user-name" />
        <Input required minLength={6} type="password" value={form.password} onChange={set("password")} placeholder="password"
          className="bg-navy-deep border-gold-dim/40" data-testid="user-password" />
        <Select value={form.role} onValueChange={set("role")}>
          <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="user-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
            {ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={form.organization_id || "__none"} onValueChange={(v) => set("organization_id")(v === "__none" ? "" : v)}>
          <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="user-org">
            <SelectValue placeholder="Organization" />
          </SelectTrigger>
          <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
            <SelectItem value="__none">— none —</SelectItem>
            {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="md:col-span-5 flex justify-end">
          <Button className="btn-gold h-11 px-6" data-testid="user-create-btn">
            <UserPlus size={14} className="mr-2" /> Create User
          </Button>
        </div>
      </form>

      <div className="glass p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
            <tr className="border-b border-gold-dim/20">
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Role</th>
              <th className="text-left p-4">Organization</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const orgName = orgs.find(o => o.id === u.organization_id)?.name || "—";
              const status = u.status || "active";
              const isActive = status === "active";
              return (
                <tr key={u.id} className="border-b border-gold-dim/10">
                  <td className="p-4 text-gold font-mono text-xs">{u.email}</td>
                  <td className="p-4 text-cream">{u.name}</td>
                  <td className="p-4">
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream h-8 text-xs w-[150px]"
                        data-testid={`user-role-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                        {ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-cream-dim">{orgName}</td>
                  <td className="p-4">
                    <button
                      onClick={() => toggleStatus(u.id, status)}
                      data-testid={`user-status-toggle-${u.id}`}
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
                        isActive
                          ? "text-[color:var(--auditiq-success,#5bbf85)] border-[color:var(--auditiq-success,#5bbf85)]/40 hover:bg-[color:var(--auditiq-success,#5bbf85)]/10"
                          : "text-steel border-steel/40 hover:bg-navy-soft"
                      }`}
                      title={`Click to ${isActive ? "disable" : "enable"}`}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => openAccess(u)}
                        className="text-gold hover:text-electric"
                        data-testid={`user-access-${u.id}`}
                        title="Page access"
                      >
                        <ShieldCheck size={14} />
                      </button>
                      <button
                        onClick={() => resetPassword(u.id, u.email)}
                        className="text-electric hover:text-gold"
                        data-testid={`user-reset-${u.id}`}
                        title="Reset password"
                      >
                        <KeyRound size={14} />
                      </button>
                      <button
                        onClick={() => del(u.id, u.email)}
                        className="text-[color:var(--auditiq-critical)] hover:text-gold"
                        data-testid={`user-delete-${u.id}`}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Temp password modal */}
      {tempPwdModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="user-reset-modal"
        >
          <div className="glass max-w-md w-full p-6 relative">
            <button
              onClick={() => setTempPwdModal(null)}
              className="absolute top-3 right-3 text-cream-dim hover:text-gold"
              data-testid="user-reset-modal-close"
            ><X size={18} /></button>
            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-2">Temporary Password</div>
            <h3 className="font-display text-xl text-cream">Share this securely with the user</h3>
            <div className="text-sm text-cream-dim mt-2">
              User <span className="text-gold font-mono">{tempPwdModal.email}</span> will be
              prompted to change this password on their next sign-in.
            </div>
            <div className="mt-5 flex items-center gap-2">
              <code className="flex-1 font-mono text-gold bg-navy-deep px-3 py-2 rounded border border-gold-dim/40 text-sm" data-testid="user-reset-modal-pwd">
                {tempPwdModal.temporary_password}
              </code>
              <Button onClick={copyTempPwd} className="btn-ghost-gold h-10 px-3" data-testid="user-reset-modal-copy">
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setTempPwdModal(null)} className="btn-gold h-10 px-4" data-testid="user-reset-modal-done">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Page Access modal */}
      {accessModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="user-access-modal"
        >
          <div className="glass max-w-2xl w-full p-6 relative">
            <button
              onClick={() => setAccessModal(null)}
              className="absolute top-3 right-3 text-cream-dim hover:text-gold"
              data-testid="user-access-modal-close"
            ><X size={18} /></button>
            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-2">Page Access</div>
            <h3 className="font-display text-xl text-cream">
              Manage access for <span className="text-gold font-mono text-base">{accessModal.email}</span>
            </h3>
            <div className="text-xs text-cream-dim mt-1">
              Check which pages/modules this user can see. Leave all unchecked to use role-based defaults.
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Button onClick={selectAllPages} className="btn-ghost-gold h-8 px-3 text-xs"
                      data-testid="user-access-select-all">Select all</Button>
              <Button onClick={clearAllPages}  className="btn-ghost-gold h-8 px-3 text-xs"
                      data-testid="user-access-clear-all">Clear</Button>
              <span className="text-xs text-cream-dim ml-2">
                {accessModal.page_access.length} / {availablePages.length} selected
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-1">
              {availablePages.map(page => {
                const checked = accessModal.page_access.includes(page);
                const slug = page.toLowerCase().replace(/\s+|\//g, "-");
                return (
                  <label key={page}
                         className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                           checked
                             ? "border-gold bg-gold/10"
                             : "border-gold-dim/30 hover:border-gold/60"
                         }`}
                         data-testid={`user-access-page-${slug}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAccessPage(page)}
                      className="accent-gold"
                      data-testid={`user-access-cb-${slug}`}
                    />
                    <span className="text-sm text-cream">{page}</span>
                  </label>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button onClick={() => setAccessModal(null)}
                      className="btn-ghost-gold h-10 px-4" data-testid="user-access-cancel">
                Cancel
              </Button>
              <Button onClick={saveAccess} disabled={accessBusy}
                      className="btn-gold h-10 px-5" data-testid="user-access-save">
                {accessBusy ? "Saving…" : "Save Access"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PortalUsers = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalUsers;
